import React, { useEffect, useRef, useState } from 'react';
import { SectionId } from '../types';

// Hero carousel images from wedding-03 collection
const heroImages = [
  '/gallery/wedding-03/1F2A0570.webp',
  '/gallery/wedding-03/1F2A0514.webp',
  '/gallery/wedding-03/1F2A0571.webp',
];

export const Hero: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [textVisible, setTextVisible] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Parallax effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const offset = window.scrollY * 0.3;
        containerRef.current.style.transform = `translateY(${offset}px) scale(1.1)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-advance carousel with cinematic timing
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Text fade out after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setTextVisible(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Preload images
  useEffect(() => {
    const loadImages = async () => {
      const promises = heroImages.map((src) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = resolve;
          img.src = src;
        });
      });
      await Promise.all(promises);
      setImagesLoaded(true);
    };
    loadImages();
  }, []);

  return (
    <section id={SectionId.HERO} className="relative h-screen w-full overflow-hidden bg-stone-900">
      {/* Background Image Carousel */}
      <div 
        ref={containerRef}
        className="absolute inset-0 w-full h-full will-change-transform"
        style={{ transform: 'scale(1.1)' }}
      >
        {heroImages.map((image, index) => (
          <div
            key={image}
            className={`absolute inset-0 transition-opacity duration-[2000ms] ease-in-out ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image}
              alt={`Wedding moment ${index + 1}`}
              className="w-full h-full object-cover"
              style={{
                filter: 'brightness(0.7) contrast(1.1) saturate(1.1)',
              }}
            />
          </div>
        ))}
        
        {/* Ken Burns zoom effect overlay */}
        <div 
          className="absolute inset-0 bg-gradient-to-b from-stone-900/20 via-transparent to-stone-900/60"
        />
      </div>

      {/* Cinematic grain overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none z-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Hero Text with Mix Blend Difference */}
      <div 
        className={`absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-20 transition-opacity duration-1000 ${
          textVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="mix-blend-difference">
          <p className="text-xs md:text-sm tracking-[0.5em] uppercase text-white mb-8 font-sans animate-fade-in">
            Timeless Indian Weddings
          </p>
          <h1 className="text-6xl md:text-8xl lg:text-[10rem] font-serif text-white leading-[0.9] mb-8 font-light tracking-tight">
            <span className="block italic animate-fade-in-up">Unveiling</span>
            <span className="block font-logo not-italic mt-2 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              Emotions
            </span>
          </h1>
          <p className="max-w-lg mx-auto text-white font-sans font-light leading-relaxed text-sm md:text-base animate-fade-in" style={{ animationDelay: '400ms' }}>
            Capturing the silence between the chaos, and the love within the traditions.
          </p>
        </div>
      </div>

      {/* Scroll Indicator - Always visible */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20">
        <button 
          onClick={() => document.getElementById(SectionId.WORK)?.scrollIntoView({ behavior: 'smooth' })}
          className="group flex flex-col items-center gap-3 text-stone-300 hover:text-stone-50 transition-colors cursor-pointer"
        >
          <span className="text-[10px] uppercase tracking-widest">Explore</span>
          <div className="w-2 h-2 rounded-full bg-stone-300 group-hover:bg-white group-hover:scale-125 transition-all duration-300 animate-bounce" />
        </button>
      </div>

      {/* Carousel Indicators */}
      <div className="absolute bottom-12 right-8 md:right-12 z-20 flex gap-2">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-8 h-[2px] transition-all duration-500 ${
              index === currentIndex ? 'bg-white' : 'bg-white/30 hover:bg-white/60'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Loading overlay */}
      {!imagesLoaded && (
        <div className="absolute inset-0 bg-stone-900 z-50 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-stone-600 border-t-white rounded-full animate-spin" />
        </div>
      )}
    </section>
  );
};